/**
 * Profile Page
 *
 * Allows users to view and update their account information including
 * display name, email, and password.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  updateProfile,
  updateEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions, db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  User,
  Mail,
  Lock,
  ArrowLeft,
  Check,
  AlertCircle,
  Shield,
  FileText,
  LockIcon,
  Unlock,
  Users,
  Bell,
  ArrowRight,
  MapPin,
} from 'lucide-react';

type ActiveSection = 'display' | 'email' | 'password' | 'contact' | null;

interface UpdateUserProfileResponse {
  success: boolean;
  error?: string;
}

// v2.2 Authorization types
type CaseFileAuthority = 'patient_self' | 'parent_of_minor' | 'legal_guardian' | 'designated_agent';
type CaseFileLockReason = 'pending_patient_authorization' | 'pending_adult_transition' | 'authorization_revoked';

interface AuthorityDocumentation {
  parentalAttestationId?: string;
  guardianshipDocumentId?: string;
  patientAuthorizationId?: string;
}

interface CaseFileSummary {
  caseFileId: string;
  name: string;
  dateOfBirth: string;
  diagnoses: string[];
  userPermissions: 'owner' | 'editor' | 'logger' | 'viewer';
  userRelationship?: string;
  isOwner: boolean;
  authorityType?: CaseFileAuthority;
  authorityDocumentation?: AuthorityDocumentation;
  isLocked?: boolean;
  lockReason?: CaseFileLockReason;
}

interface ListCaseFilesResponse {
  success: boolean;
  caseFiles: CaseFileSummary[];
  totalCount: number;
}

interface PendingAuthorization {
  authorizationDocumentId: string;
  caseFileId: string;
  caseFileName: string;
  requestedBy: {
    name: string;
    email: string;
    relationship: string;
  };
  createdAt: string;
}

interface PendingAuthorizationsResponse {
  success: boolean;
  authorizations: PendingAuthorization[];
  totalCount: number;
}

export function Profile(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State for display name update
  const [displayName, setDisplayName] = useState(user?.displayName || '');

  // State for email update
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  // State for password update
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [activeSection, setActiveSection] = useState<ActiveSection>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // CaseFiles state
  const [caseFiles, setCaseFiles] = useState<CaseFileSummary[]>([]);
  const [caseFilesLoading, setCaseFilesLoading] = useState(true);

  // Pending authorizations state
  const [pendingAuthorizations, setPendingAuthorizations] = useState<PendingAuthorization[]>([]);
  const [pendingAuthLoading, setPendingAuthLoading] = useState(true);

  // Contact information state (from Firestore user document)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [contactInfoLoading, setContactInfoLoading] = useState(true);
  const [contactInfoSummary, setContactInfoSummary] = useState('');

  const userDisplayName = user?.displayName || user?.email?.split('@')[0] || 'User';

  // Fetch CaseFiles on mount
  useEffect(() => {
    const fetchCaseFiles = async () => {
      try {
        const listUserCaseFiles = httpsCallable<Record<string, never>, ListCaseFilesResponse>(
          functions,
          'listUserCaseFiles'
        );
        const result = await listUserCaseFiles({});
        if (result.data.success) {
          setCaseFiles(result.data.caseFiles || []);
        }
      } catch (err) {
        console.error('Error fetching CaseFiles:', err);
      } finally {
        setCaseFilesLoading(false);
      }
    };
    fetchCaseFiles();
  }, []);

  // Fetch pending authorizations on mount
  useEffect(() => {
    const fetchPendingAuthorizations = async () => {
      try {
        const getPendingAuthorizations = httpsCallable<
          Record<string, never>,
          PendingAuthorizationsResponse
        >(functions, 'getPendingAuthorizations');
        const result = await getPendingAuthorizations({});
        if (result.data.success) {
          setPendingAuthorizations(result.data.authorizations || []);
        }
      } catch (err) {
        // Silently fail - this is not critical
        console.log('No pending authorizations or error checking:', err);
      } finally {
        setPendingAuthLoading(false);
      }
    };
    fetchPendingAuthorizations();
  }, []);

  // Fetch contact information from Firestore user document
  useEffect(() => {
    if (!user?.uid) return;

    const fetchContactInfo = async () => {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          setPhoneNumber(data.phoneNumber || '');
          setAddress(data.address || '');
          setCity(data.city || '');
          setState(data.state || '');
          setZipCode(data.zipCode || '');

          // Build summary for display
          const parts: string[] = [];
          if (data.phoneNumber) parts.push(data.phoneNumber);
          if (data.address || data.city || data.state) {
            const addrParts = [data.address, data.city, data.state, data.zipCode].filter(Boolean);
            parts.push(addrParts.join(', '));
          }
          setContactInfoSummary(parts.length > 0 ? parts.join(' · ') : 'Not set');
        } else {
          setContactInfoSummary('Not set');
        }
      } catch (err) {
        console.error('Error fetching contact info:', err);
        setContactInfoSummary('Not set');
      } finally {
        setContactInfoLoading(false);
      }
    };

    fetchContactInfo();
  }, [user?.uid]);

  const userInitial = userDisplayName.charAt(0).toUpperCase();

  // Format authority type for display
  const formatAuthorityType = (type?: CaseFileAuthority): string => {
    if (!type) return 'Not set';
    const labels: Record<CaseFileAuthority, string> = {
      patient_self: 'Patient Self',
      parent_of_minor: 'Parent of Minor',
      legal_guardian: 'Legal Guardian',
      designated_agent: 'Designated Agent',
    };
    return labels[type] || type;
  };

  // Format lock reason for display
  const formatLockReason = (reason?: CaseFileLockReason): string => {
    if (!reason) return '';
    const labels: Record<CaseFileLockReason, string> = {
      pending_patient_authorization: 'Pending patient authorization',
      pending_adult_transition: 'Pending adult transition',
      authorization_revoked: 'Authorization revoked',
    };
    return labels[reason] || reason;
  };

  // Get authorization documentation status
  const getAuthDocStatus = (authDoc?: AuthorityDocumentation): string => {
    if (!authDoc) return 'None required';
    if (authDoc.parentalAttestationId) return 'Parental attestation on file';
    if (authDoc.guardianshipDocumentId) return 'Guardianship document uploaded';
    if (authDoc.patientAuthorizationId) return 'Patient authorization on file';
    return 'None required';
  };

  // Get owner relationship from userRelationship when isOwner is true
  const getOwnerRelationship = (cf: CaseFileSummary): string => {
    if (!cf.isOwner) return cf.userRelationship || 'Care team member';
    // For owner, infer relationship from authorityType
    if (cf.authorityType === 'patient_self') return 'Self';
    if (cf.authorityType === 'parent_of_minor') return 'Parent';
    if (cf.authorityType === 'legal_guardian') return 'Guardian';
    if (cf.authorityType === 'designated_agent') return 'Designated Agent';
    return 'Owner';
  };

  const resetForm = () => {
    setActiveSection(null);
    setError(null);
    setSuccess(null);
    setDisplayName(user?.displayName || '');
    setNewEmail('');
    setEmailPassword('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleUpdateDisplayName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Update Firebase Auth
      await updateProfile(user, { displayName: displayName.trim() });

      // Also sync to Firestore so CaseMaker can read it
      const updateUserProfile = httpsCallable<
        { displayName: string },
        UpdateUserProfileResponse
      >(functions, 'updateUserProfileHttp');
      await updateUserProfile({ displayName: displayName.trim() });

      setSuccess('Display name updated successfully');
      setActiveSection(null);
    } catch (err) {
      console.error('Error updating display name:', err);
      setError('Failed to update display name. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.email) return;

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      // Re-authenticate user before changing email
      const credential = EmailAuthProvider.credential(user.email, emailPassword);
      await reauthenticateWithCredential(user, credential);

      // Update email in Firebase Auth
      await updateEmail(user, newEmail.trim());

      // Sync new email to Firestore so CaseMaker uses it
      try {
        const updateUserProfile = httpsCallable<
          { syncEmail: boolean },
          UpdateUserProfileResponse
        >(functions, 'updateUserProfileHttp');
        await updateUserProfile({ syncEmail: true });
      } catch (syncErr) {
        // Non-fatal: Auth email updated, Firestore will catch up on next profile save
        console.warn('Firestore email sync failed (will retry on next save):', syncErr);
      }

      // Send verification email to new address
      await sendEmailVerification(user);

      setSuccess('Email updated. Please check your new email for verification.');
      setNewEmail('');
      setEmailPassword('');
      setActiveSection(null);
    } catch (err: unknown) {
      console.error('Error updating email:', err);
      if (err instanceof Error && 'code' in err) {
        const errorCode = (err as { code: string }).code;
        if (errorCode === 'auth/wrong-password') {
          setError('Incorrect password. Please try again.');
        } else if (errorCode === 'auth/email-already-in-use') {
          setError('This email is already in use by another account.');
        } else if (errorCode === 'auth/invalid-email') {
          setError('Please enter a valid email address.');
        } else {
          setError('Failed to update email. Please try again.');
        }
      } else {
        setError('Failed to update email. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !user.email) return;

    setError(null);
    setSuccess(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsSubmitting(true);

    try {
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setActiveSection(null);
    } catch (err: unknown) {
      console.error('Error updating password:', err);
      if (err instanceof Error && 'code' in err) {
        const errorCode = (err as { code: string }).code;
        if (errorCode === 'auth/wrong-password') {
          setError('Current password is incorrect');
        } else if (errorCode === 'auth/weak-password') {
          setError('Password is too weak. Please choose a stronger password.');
        } else {
          setError('Failed to update password. Please try again.');
        }
      } else {
        setError('Failed to update password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateContactInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setError(null);
    setSuccess(null);
    setIsSubmitting(true);

    try {
      const updateUserProfile = httpsCallable<
        {
          phoneNumber?: string;
          address?: string;
          city?: string;
          state?: string;
          zipCode?: string;
        },
        UpdateUserProfileResponse
      >(functions, 'updateUserProfileHttp');

      await updateUserProfile({
        phoneNumber: phoneNumber.trim(),
        address: address.trim(),
        city: city.trim(),
        state: state.trim(),
        zipCode: zipCode.trim(),
      });

      // Rebuild summary
      const parts: string[] = [];
      if (phoneNumber.trim()) parts.push(phoneNumber.trim());
      const addrParts = [address.trim(), city.trim(), state.trim(), zipCode.trim()].filter(Boolean);
      if (addrParts.length > 0) parts.push(addrParts.join(', '));
      setContactInfoSummary(parts.length > 0 ? parts.join(' · ') : 'Not set');

      setSuccess('Contact information updated successfully');
      setActiveSection(null);
    } catch (err) {
      console.error('Error updating contact info:', err);
      setError('Failed to update contact information. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendVerificationEmail = async () => {
    if (!user) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await sendEmailVerification(user);
      setSuccess('Verification email sent. Please check your inbox.');
    } catch (err) {
      console.error('Error sending verification email:', err);
      setError('Failed to send verification email. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-airy">
      <header className="bg-white border-b border-soft-neutral">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-display text-deep-forest font-light">Hesvara</h1>
          <button onClick={() => navigate('/')} className="btn-ghost btn-sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </button>
        </div>
      </header>

      <main id="main-content" tabIndex={-1} className="max-w-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8 outline-none">
        <div className="mb-8">
          <h2 className="text-2xl font-display text-deep-forest mb-2">Profile Settings</h2>
          <p className="text-muted">Manage your account information and security settings</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success mb-6 flex items-center gap-2">
            <Check className="w-5 h-5" />
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error mb-6 flex items-center gap-2" role="alert">
            <AlertCircle className="w-5 h-5" aria-hidden="true" />
            {error}
          </div>
        )}

        {/* Profile Avatar Section */}
        <div className="card mb-6">
          <div className="card-body flex items-center gap-6">
            <div className="w-20 h-20 bg-evergreen/10 rounded-full flex items-center justify-center">
              <span className="text-evergreen font-semibold text-3xl">{userInitial}</span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-ink">{userDisplayName}</h3>
              <p className="text-muted">{user?.email}</p>
              {user?.emailVerified ? (
                <span className="inline-flex items-center gap-1 text-sm text-evergreen mt-1">
                  <Shield className="w-4 h-4" />
                  Email verified
                </span>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-amber-600">Email not verified</span>
                  <button
                    onClick={handleSendVerificationEmail}
                    disabled={isSubmitting}
                    className="text-sm text-evergreen hover:underline"
                  >
                    Send verification
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Display Name Section */}
        <div className="card mb-4">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Display Name</h3>
                <p className="text-sm text-muted">{user?.displayName || 'Not set'}</p>
              </div>
            </div>
            {activeSection !== 'display' && (
              <button
                onClick={() => {
                  resetForm();
                  setActiveSection('display');
                }}
                className="btn-secondary btn-sm"
              >
                Edit
              </button>
            )}
          </div>

          {activeSection === 'display' && (
            <div className="card-body border-t border-soft-neutral">
              <form onSubmit={handleUpdateDisplayName} className="space-y-4">
                <div>
                  <label htmlFor="displayName" className="label">
                    Display Name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="input"
                    placeholder="Enter your display name"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn-primary">
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Email Section */}
        <div className="card mb-4">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Email Address</h3>
                <p className="text-sm text-muted">{user?.email}</p>
              </div>
            </div>
            {activeSection !== 'email' && (
              <button
                onClick={() => {
                  resetForm();
                  setActiveSection('email');
                }}
                className="btn-secondary btn-sm"
              >
                Change
              </button>
            )}
          </div>

          {activeSection === 'email' && (
            <div className="card-body border-t border-soft-neutral">
              <form onSubmit={handleUpdateEmail} className="space-y-4">
                <div>
                  <label htmlFor="newEmail" className="label">
                    New Email Address
                  </label>
                  <input
                    id="newEmail"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="input"
                    placeholder="Enter new email address"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="emailPassword" className="label">
                    Current Password
                  </label>
                  <input
                    id="emailPassword"
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    className="input"
                    placeholder="Enter your current password"
                    required
                  />
                  <p className="helper-text">Required to confirm this change</p>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn-primary">
                    {isSubmitting ? 'Updating...' : 'Update Email'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Password Section */}
        <div className="card mb-4">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <Lock className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Password</h3>
                <p className="text-sm text-muted">Last changed: Unknown</p>
              </div>
            </div>
            {activeSection !== 'password' && (
              <button
                onClick={() => {
                  resetForm();
                  setActiveSection('password');
                }}
                className="btn-secondary btn-sm"
              >
                Change
              </button>
            )}
          </div>

          {activeSection === 'password' && (
            <div className="card-body border-t border-soft-neutral">
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div>
                  <label htmlFor="currentPassword" className="label">
                    Current Password
                  </label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input"
                    placeholder="Enter current password"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="label">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                  />
                  <p className="helper-text">Must be at least 8 characters</p>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input"
                    placeholder="Confirm new password"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn-primary">
                    {isSubmitting ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Contact Information Section */}
        <div className="card mb-4">
          <div className="card-header flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
                <MapPin className="w-5 h-5 text-evergreen" />
              </div>
              <div>
                <h3 className="font-semibold text-ink">Contact Information</h3>
                <p className="text-sm text-muted">
                  {contactInfoLoading ? 'Loading...' : contactInfoSummary}
                </p>
              </div>
            </div>
            {activeSection !== 'contact' && (
              <button
                onClick={() => {
                  resetForm();
                  setActiveSection('contact');
                }}
                className="btn-secondary btn-sm"
              >
                Edit
              </button>
            )}
          </div>

          {activeSection === 'contact' && (
            <div className="card-body border-t border-soft-neutral">
              <p className="text-sm text-muted mb-4">
                This information is used in generated advocacy letters (signature block, return address).
                All fields are optional.
              </p>
              <form onSubmit={handleUpdateContactInfo} className="space-y-4">
                <div>
                  <label htmlFor="phoneNumber" className="label">
                    Phone Number
                  </label>
                  <input
                    id="phoneNumber"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="input"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="address" className="label">
                    Street Address
                  </label>
                  <input
                    id="address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="input"
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="grid grid-cols-6 gap-3">
                  <div className="col-span-3">
                    <label htmlFor="city" className="label">
                      City
                    </label>
                    <input
                      id="city"
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      className="input"
                      placeholder="Seattle"
                    />
                  </div>
                  <div className="col-span-1">
                    <label htmlFor="state" className="label">
                      State
                    </label>
                    <input
                      id="state"
                      type="text"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      className="input"
                      placeholder="WA"
                    />
                  </div>
                  <div className="col-span-2">
                    <label htmlFor="zipCode" className="label">
                      ZIP Code
                    </label>
                    <input
                      id="zipCode"
                      type="text"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      className="input"
                      placeholder="98101"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    disabled={isSubmitting}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn-primary">
                    {isSubmitting ? 'Saving...' : 'Save Contact Info'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Pending Authorizations Section */}
        {!pendingAuthLoading && pendingAuthorizations.length > 0 && (
          <div className="card mb-4 border-amber-200 bg-amber-50/30">
            <div className="card-header flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center relative">
                  <Bell className="w-5 h-5 text-amber-600" />
                  <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {pendingAuthorizations.length}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-ink">Pending Authorization Requests</h3>
                  <p className="text-sm text-muted">
                    {pendingAuthorizations.length === 1
                      ? 'Someone has requested access to your healthcare information'
                      : `${pendingAuthorizations.length} people have requested access to your healthcare information`}
                  </p>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="space-y-3">
                {pendingAuthorizations.map((auth) => (
                  <div
                    key={auth.authorizationDocumentId}
                    className="bg-white border border-amber-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-ink">{auth.caseFileName}</p>
                        <p className="text-sm text-muted">
                          Requested by: <span className="font-medium">{auth.requestedBy.name}</span>
                          {auth.requestedBy.relationship && (
                            <span className="text-muted"> ({auth.requestedBy.relationship})</span>
                          )}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          Received: {new Date(auth.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate(`/authorization/${auth.authorizationDocumentId}`, {
                          state: { returnTo: '/profile' }
                        })}
                        className="btn-secondary btn-sm flex items-center gap-1"
                      >
                        Review
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {pendingAuthorizations.length > 1 && (
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <button
                    onClick={() => navigate('/authorization')}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    Review All Requests
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Account Info */}
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-ink">Account Information</h3>
          </div>
          <div className="card-body">
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted">User ID</dt>
                <dd className="font-mono text-ink">{user?.uid?.slice(0, 12)}...</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Account Created</dt>
                <dd className="text-ink">
                  {user?.metadata?.creationTime
                    ? new Date(user.metadata.creationTime).toLocaleDateString()
                    : 'Unknown'}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted">Last Sign In</dt>
                <dd className="text-ink">
                  {user?.metadata?.lastSignInTime
                    ? new Date(user.metadata.lastSignInTime).toLocaleDateString()
                    : 'Unknown'}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* My CaseFiles - Authorization Status Section */}
        <div className="card mt-6">
          <div className="card-header flex items-center gap-3">
            <div className="w-10 h-10 bg-evergreen/10 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-evergreen" />
            </div>
            <div>
              <h3 className="font-semibold text-ink">My CaseFiles</h3>
              <p className="text-sm text-muted">Authorization and access status</p>
            </div>
          </div>
          <div className="card-body">
            {caseFilesLoading ? (
              <p className="text-muted text-sm">Loading CaseFiles...</p>
            ) : caseFiles.length === 0 ? (
              <p className="text-muted text-sm">No CaseFiles found.</p>
            ) : (
              <div className="space-y-4">
                {caseFiles.map((cf) => (
                  <div
                    key={cf.caseFileId}
                    className="border border-soft-neutral rounded-lg p-4 hover:bg-airy/50 transition-colors"
                  >
                    {/* CaseFile Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-ink">{cf.name}</h4>
                        <p className="text-sm text-muted">
                          {cf.isOwner ? 'You are the owner' : 'Shared with you'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {cf.isLocked ? (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-sm">
                            <LockIcon className="w-4 h-4" />
                            Locked
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-evergreen text-sm">
                            <Unlock className="w-4 h-4" />
                            Active
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Authorization Details */}
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="flex justify-between sm:flex-col sm:gap-0.5">
                        <dt className="text-muted">Authority Type</dt>
                        <dd className="text-ink font-medium">{formatAuthorityType(cf.authorityType)}</dd>
                      </div>
                      <div className="flex justify-between sm:flex-col sm:gap-0.5">
                        <dt className="text-muted">Your Relationship</dt>
                        <dd className="text-ink font-medium">{getOwnerRelationship(cf)}</dd>
                      </div>
                      <div className="flex justify-between sm:flex-col sm:gap-0.5">
                        <dt className="text-muted">Lock Status</dt>
                        <dd className="text-ink font-medium">
                          {cf.isLocked ? (
                            <span className="text-amber-600">
                              {formatLockReason(cf.lockReason) || 'Locked'}
                            </span>
                          ) : (
                            <span className="text-evergreen">Unlocked</span>
                          )}
                        </dd>
                      </div>
                      <div className="flex justify-between sm:flex-col sm:gap-0.5">
                        <dt className="text-muted">Authorization Docs</dt>
                        <dd className="text-ink font-medium">{getAuthDocStatus(cf.authorityDocumentation)}</dd>
                      </div>
                      <div className="flex justify-between sm:flex-col sm:gap-0.5">
                        <dt className="text-muted">Your Permission</dt>
                        <dd className="text-ink font-medium capitalize">{cf.userPermissions}</dd>
                      </div>
                    </dl>

                    {/* View CaseFile Link */}
                    <div className="mt-3 pt-3 border-t border-soft-neutral">
                      <button
                        onClick={() => navigate(`/casefile/${cf.caseFileId}`)}
                        className="text-sm text-evergreen hover:underline flex items-center gap-1"
                      >
                        <Users className="w-4 h-4" />
                        View CaseFile Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Profile;
